window.PackageProtection = {
    forceEnableOnce: false,
    mutationQueue: Promise.resolve(),
    mutationQueueDepth: 0,
    cartCache: null,
    cartPromise: null,
    cartRequestId: 0,
    variantsCacheKey: null,
    variantsCache: [],
  
    /* ------------------ HELPERS ------------------ */

    setCart(cart) {
      this.cartRequestId += 1;
      this.cartCache = cart || null;
      this.cartPromise = null;
      return this.cartCache;
    },

    clearCartCache() {
      this.cartRequestId += 1;
      this.cartCache = null;
      this.cartPromise = null;
    },

    async request(url, body) {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        let message = `Request failed (${response.status})`;

        try {
          const error = await response.clone().json();
          message = error.description || error.message || message;
        } catch (jsonError) {
          try {
            const text = await response.text();
            if (text) message = text;
          } catch (textError) {
          }
        }

        throw new Error(message);
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        return response.json();
      }

      return null;
    },
  
    async getCart(options = {}) {
      const force = options.force === true;

      if (force) {
        this.clearCartCache();
      }

      if (this.cartCache) {
        return this.cartCache;
      }

      if (!this.cartPromise) {
        const requestId = ++this.cartRequestId;
        const request = fetch("/cart.js")
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to load cart (${response.status})`);
            }

            return response.json();
          })
          .then((cart) => {
            if (requestId === this.cartRequestId) {
              this.cartCache = cart;
            }
            return cart;
          })
          .finally(() => {
            if (this.cartPromise === request) {
              this.cartPromise = null;
            }
          });

        this.cartPromise = request;
      }

      return this.cartPromise;
    },
  
    getVariants() {
      const drawer = document.querySelector("cart-drawer");
      if (!drawer) return [];

      const el = drawer.querySelector("#ProtectionVariants");
      if (!el) return [];

      const cacheKey = el.textContent || "";
      if (this.variantsCacheKey === cacheKey) {
        return this.variantsCache;
      }

      try {
        const variants = JSON.parse(cacheKey)
          .filter((variant) => Number(variant?.id) > 0)
          .sort((a, b) => a.price - b.price);

        this.variantsCacheKey = cacheKey;
        this.variantsCache = variants;
        return variants;
      } catch {
        this.variantsCacheKey = cacheKey;
        this.variantsCache = [];
        return [];
      }
    },
  
    getProtectionItems(cart) {
      return cart.items.filter(i => i.properties?._package_protection);
    },
  
    getRealItems(cart) {
      return cart.items.filter(i => !i.properties?._package_protection);
    },

    isCartSnapshot(cart) {
      return !!cart && Array.isArray(cart.items) && typeof cart === "object";
    },

    getSectionRequestPayload() {
      const drawer = document.querySelector("cart-drawer");
      if (!drawer || typeof drawer.getSectionsToRender !== "function") {
        return {};
      }

      const sections = drawer.getSectionsToRender().map((section) => section.id);
      if (!sections.length) return {};

      return {
        sections,
        sections_url: window.location.pathname
      };
    },
  
    calcCartTotalWithoutProtection(cart) {
      return this.getRealItems(cart).reduce(
        (sum, item) => sum + Number(item.final_line_price || 0),
        0
      );
    },

    getTargetVariant(variants, merchandiseTotalCents) {
      if (!variants.length) return null;

      // Keep the same 1.9% tier calculation used on new-theme-dev. Cart
      // values stay in cents until the comparison to avoid stale/rounded
      // display values affecting the selected protection variant.
      const targetMinimumCents = merchandiseTotalCents * 0.019;

      return (
        variants.find(
          (variant) => Math.round(Number(variant.price) * 100) >= targetMinimumCents
        ) ||
        variants[variants.length - 1]
      );
    },

    buildState(cart, variants = this.getVariants()) {
      const disabled =
        cart.attributes?._protection_disabled_by_user === "true";
      const items = this.getProtectionItems(cart);
      const active = items.length > 0 && !disabled;
      const actualPrice =
        items.reduce(
          (sum, item) => sum + Number(item.final_line_price || 0),
          0
        ) / 100;

      if (!variants.length) {
        return {
          active,
          // The active line-item price is authoritative even if the variant
          // configuration is temporarily unavailable during a drawer render.
          price: items.length ? `$${actualPrice.toFixed(2)}` : null,
          disabled,
          synchronized: false,
          changed: false,
          error: false,
          cart,
          targetVariantId: null
        };
      }

      const merchandiseTotalCents = this.calcCartTotalWithoutProtection(cart);
      const target = this.getTargetVariant(variants, merchandiseTotalCents);
      const synchronized =
        active &&
        items.length === 1 &&
        items[0].variant_id === target?.id;

      return {
        active,
        price: active
          ? `$${actualPrice.toFixed(2)}`
          : target
            ? `$${target.price.toFixed(2)}`
            : null,
        disabled,
        synchronized,
        changed: false,
        error: false,
        cart,
        targetVariantId: target?.id || null
      };
    },

    async updateAttributes(attributes) {
      const cart = await this.request("/cart/update.js", { attributes });
      this.setCart(cart);
      return cart;
    },

    async removeProtectionItems(items) {
      if (!Array.isArray(items) || !items.length) return null;

      const updates = items.reduce((result, item) => {
        if (item?.key) {
          result[item.key] = 0;
        }
        return result;
      }, {});

      if (!Object.keys(updates).length) return null;

      const cart = await this.request("/cart/update.js", {
        updates,
        ...this.getSectionRequestPayload()
      });
      this.setCart(cart);
      return cart;
    },
  
    async addVariant(variantId) {
      const response = await this.request("/cart/add.js", {
        id: variantId,
        quantity: 1,
        properties: { _package_protection: true },
        ...this.getSectionRequestPayload()
      });
      this.clearCartCache();
      return response?.sections || null;
    },
  
    async removeByKey(key) {
      const cart = await this.request("/cart/change.js", {
        id: key,
        quantity: 0
      });
      this.setCart(cart);
      return cart;
    },
      
    /* ------------------ CORE SYNC ------------------ */
      
    enqueueMutation(operation, cart = null) {
      const cartSnapshot = this.isCartSnapshot(cart) ? cart : null;
      const safeSnapshot = this.mutationQueueDepth === 0 ? cartSnapshot : null;
      this.mutationQueueDepth += 1;

      const run = async () => {
        try {
          return await operation(safeSnapshot);
        } finally {
          this.mutationQueueDepth = Math.max(0, this.mutationQueueDepth - 1);
        }
      };

      this.mutationQueue = this.mutationQueue.then(
        run,
        run
      );
      return this.mutationQueue;
    },

    sync(cart = null) {
      return this.enqueueMutation(
        (cartSnapshot) => this._actualSync(cartSnapshot),
        cart
      );
    },

    async _actualSync(cartSnapshot = null) {
      try {
        // Reuse a complete cart returned by change/update requests. Product-add
        // responses are not full carts, so those paths still fetch /cart.js.
        const cart = this.isCartSnapshot(cartSnapshot)
          ? this.setCart(cartSnapshot)
          : await this.getCart({ force: true });
        const variants = this.getVariants();
        const protectionItems = this.getProtectionItems(cart);
        const realItems = this.getRealItems(cart);

        if (!variants.length) {
          return this.buildState(cart, variants);
        }

        /* 1️⃣ NEMA PROIZVODA → OBRIŠI PROTECTION */
        if (realItems.length === 0) {
          if (!protectionItems.length) {
            return this.buildState(cart, variants);
          }

          const cleanedCart =
            await this.removeProtectionItems(protectionItems) ||
            await this.getCart({ force: true });

          return {
            ...this.buildState(cleanedCart, variants),
            changed: true
          };
        }

        const state = this.buildState(cart, variants);

        /* 2️⃣ USER GA JE RUČNO UGASIO → NIŠTA */
        if (state.disabled && !this.forceEnableOnce) {
          return state;
        }

        if (state.synchronized) {
          return state;
        }

        let nextCart = cart;

        if (protectionItems.length) {
          nextCart =
            await this.removeProtectionItems(protectionItems) || nextCart;
        }

        if (state.targetVariantId) {
          const sections = await this.addVariant(state.targetVariantId);
          nextCart = await this.getCart({ force: true });
          if (sections) nextCart.sections = sections;
        }

        return {
          ...this.buildState(nextCart, variants),
          changed: true
        };
      } catch (err) {
        console.error("[PackageProtection.sync]", err);
        try {
          return {
            ...this.buildState(await this.getCart({ force: true })),
            error: true
          };
        } catch (reloadError) {
          return {
            active: false,
            price: null,
            disabled: false,
            synchronized: false,
            changed: false,
            error: true,
            cart: null,
            targetVariantId: null
          };
        }
      } finally {
        this.forceEnableOnce = false;
      }
    },
  
    /* ------------------ DISABLE ------------------ */
  
    disable(cartSnapshot = null) {
      return this.enqueueMutation(
        async (safeSnapshot) => {
          try {
            return await this._actualDisable(safeSnapshot);
          } catch (firstError) {
            // A cart restored from checkout can briefly expose stale line-item
            // keys. Reload once and complete the same user action instead of
            // requiring a second click.
            console.warn("[PackageProtection.disable] Retrying with a fresh cart", firstError);
            return this._actualDisable();
          }
        },
        cartSnapshot
      );
    },

    async _actualDisable(cartSnapshot = null) {
      const cart = this.isCartSnapshot(cartSnapshot)
        ? this.setCart(cartSnapshot)
        : await this.getCart({ force: true });

      const items = this.getProtectionItems(cart);
      const needsOptOut =
        cart.attributes?._protection_disabled_by_user !== "true";
      const changed = needsOptOut || items.length > 0;

      if (!changed) return { ...this.buildState(cart), changed: false };

      const updates = items.reduce((result, item) => {
        if (item?.key) result[item.key] = 0;
        return result;
      }, {});
      const payload = {
        attributes: { _protection_disabled_by_user: "true" },
        ...this.getSectionRequestPayload()
      };
      if (Object.keys(updates).length) payload.updates = updates;

      // Apply the opt-out and line removal in one Shopify cart mutation. This
      // avoids the partially-disabled state that previously made the first
      // attempt fail while the second one succeeded.
      const nextCart = await this.request("/cart/update.js", payload);
      this.setCart(nextCart);

      if (
        nextCart.attributes?._protection_disabled_by_user !== "true" ||
        this.getProtectionItems(nextCart).length > 0
      ) {
        throw new Error("Package protection removal was not persisted");
      }

      return { ...this.buildState(nextCart), changed };
    },
  
    /* ------------------ ENABLE ------------------ */
  
    enable(cartSnapshot = null) {
      return this.enqueueMutation(
        (safeSnapshot) => this._actualEnable(safeSnapshot),
        cartSnapshot
      );
    },

    async _actualEnable(cartSnapshot = null) {
      let cart = this.isCartSnapshot(cartSnapshot)
        ? this.setCart(cartSnapshot)
        : await this.getCart({ force: true });

      let attributesChanged = false;
      if (cart.attributes?._protection_disabled_by_user === "true") {
        cart = await this.updateAttributes({
          _protection_disabled_by_user: null
        });
        attributesChanged = true;
      }

      this.forceEnableOnce = true;
      const state = await this._actualSync(cart);
      return {
        ...state,
        changed: attributesChanged || state.changed
      };
    },
  
    /* ------------------ UI ------------------ */
  
    toggle(cartSnapshot = null) {
      return this.enqueueMutation(
        (safeSnapshot) => this._actualToggle(safeSnapshot),
        cartSnapshot
      );
    },

    async _actualToggle(cartSnapshot = null) {
      const cart = this.isCartSnapshot(cartSnapshot)
        ? this.setCart(cartSnapshot)
        : await this.getCart({ force: true });
      const items = this.getProtectionItems(cart);
      const disabled =
        cart.attributes?._protection_disabled_by_user === "true";

      if (items.length && !disabled) {
        return this._actualDisable(cart);
      } else {
        return this._actualEnable(cart);
      }
    },
  
    async onCartMutation(cart = null) {
      return this.sync(cart);
    },
  
    async getState(options = {}) {
      const cart = await this.getCart({ ...options, force: options.force !== false });
      return this.buildState(cart);
    }
      
};
